import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { agentService, utilService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button } from '../../../atoms'
import { EditTable } from '../../../organisms'
import { Panel } from '../../../atoms'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'
import { COLORS } from 'static/constants'
import ClassNames from 'classnames'
import { useDebounce } from '../../../../utils/hooks'

declare var window: interfaces.IWindow

interface IProps {
  meetup_places: interfaces.IMeetupPlace[]
  user: any
}

const COLUMNS = [
  {
    name: I18n.t('generic.area'),
    field: 'area',
    required: true,
  },
  {
    name: 'sub_area',
    field: 'sub_area',
    required: true,
  },
  {
    name: 'name_ja',
    field: 'name_ja',
    required: true,
  },
  {
    name: 'name_en',
    field: 'name_en',
    required: true,
  },
]

const MAX_DISPLAY = 50

const AgentMeetupPlaces: React.FC<IProps> = ({ meetup_places: meetupPlaces, user }) => {
  Hooks.SetDocumentTitle('Agent Meetup Places')
  const { data: userData } = utilService.getDataFromJson(user)
  const [keyword, setKeyword] = React.useState('')
  const [filteredMeetupPlaces, setFilteredMeetupPlaces] = React.useState<interfaces.IMeetupPlace[]>(
    meetupPlaces
  )
  const debouncedKeyword = useDebounce<string>(keyword, 600)

  React.useEffect(() => {
    // 2文字未満の場合は全件表示
    if (debouncedKeyword.length < 2) {
      setFilteredMeetupPlaces(meetupPlaces)
      return
    }

    const separated = debouncedKeyword
      .toLowerCase()
      .split(/[\s　]+/)
      .filter(k => k.trim() !== '')

    if (separated.length === 0) {
      setFilteredMeetupPlaces(meetupPlaces)
      return
    }

    const filtered = meetupPlaces.filter(place => {
      return separated.every(query => {
        return (
          place.sub_area?.toLowerCase().includes(query) ||
          place.name_ja?.toLowerCase().includes(query) ||
          place.name_en?.toLowerCase().includes(query)
        )
      })
    })

    setFilteredMeetupPlaces(filtered)
  }, [debouncedKeyword])

  const handleClearFilter = () => {
    setKeyword('')
    setFilteredMeetupPlaces(meetupPlaces)
  }

  const handleDelete = async id => {
    const { flush } = await agentService.deleteMeetupPlace(id)
    window.flashMessages.addMessage({
      text: flush.message,
      type: flush.type,
    })
    location.reload()
  }

  const handleClickDelete = record => {
    if (window.confirm(I18n.t('generic.delete_confirmation'))) {
      handleDelete(record.id)
    }
  }

  const counterText = React.useMemo(() => {
    return `${Math.min(filteredMeetupPlaces.length, MAX_DISPLAY)}件  / ${
      filteredMeetupPlaces.length
    }件`
  }, [filteredMeetupPlaces, filteredMeetupPlaces.length])

  const renderRecordControls = record => {
    return (
      <>
        <Button key="custom" small={true}>
          <a href={`/agent/meetup_places/${record.id}/edit`}>{I18n.t('generic.edit')}</a>
        </Button>
        <Button key="delete" small={true} handleClick={() => handleClickDelete(record)}>
          {I18n.t('generic.delete')}
        </Button>
      </>
    )
  }
  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel
          title={
            <Heading>
              <span>Meetup Place</span>
              <div className="rightControls">
                <Search className={ClassNames({ hasValue: keyword.length > 0 })}>
                  <SvgIcon
                    name="filter_list"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  <input
                    name="search"
                    className="searchInput"
                    onChange={e => setKeyword(e.target.value)}
                    value={keyword}
                    placeholder="エリア、ホテル名検索"
                    aria-label="エリア、ホテル名検索"
                  />
                  <div onClick={handleClearFilter} className="clearFilter" role="button">
                    <SvgIcon name="cancel" className="icon" ariaHidden={true} role="presentation" />
                  </div>
                </Search>
                <Button small={true}>
                  <a href="/agent/meetup_places/new">{I18n.t('generic.create_new')}</a>
                </Button>
              </div>
            </Heading>
          }
        >
          <EditTable
            editable={true}
            columns={COLUMNS}
            records={filteredMeetupPlaces.slice(0, MAX_DISPLAY)}
            pagination={null}
            handleDelete={null}
            getEditLink={null}
            renderRecordControls={renderRecordControls}
          />
          <p className="counter">{counterText}</p>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  .rightControls {
    flex: 0;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const Search = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  width: 240px;
  height: 32px;
  gap: 4px;
  color: ${COLORS.subText};
  border: 1px solid ${COLORS.divider};
  border-radius: 4px;
  padding: 4px;
  .icon {
    width: 20px;
    height: 20px;
    flex: none;
  }
  .clearFilter {
    display: none;
    flex: 0 0 auto;
    .icon {
      width: 20px;
      height: 20px;
      color: ${COLORS.subText};
      transition: color 0.2s;
      &:hover {
        color: ${COLORS.black};
      }
    }
  }
  &.hasValue {
    border-bottom-color: ${COLORS.divider};
    .clearFilter {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }
  .searchInput {
    width: 100%;
    height: 24px;
    padding: 0 0 2px;
    outline: none;
    border: none;
    background-color: transparent;
    color: ${COLORS.subText};
    :focus {
      color: ${COLORS.black};
    }
  }
`
const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .counter {
    margin-top: 8px;
    font-size: 14px;
    text-align: center;
    color: ${COLORS.subText};
  }
`
export default AgentMeetupPlaces
